import { Injectable } from '@angular/core';
import { getApp, getApps, initializeApp, FirebaseApp } from 'firebase/app';
import {
  Auth,
  ActionCodeSettings,
  applyActionCode,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  sendSignInLinkToEmail,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailLink,
  signInWithRedirect,
  signOut,
  User,
  UserCredential,
} from 'firebase/auth';
import { environment } from 'src/environments/environment';
import {
  awaitFirebasePersistenceReady,
  getFirebaseRedirectBootstrapState,
  getSharedFirebaseApp,
  getSharedFirebaseAuth,
} from 'src/firebase-social-bootstrap';
import { getFirebaseInitOptions } from 'src/app/config/firebase-config';

export type FirebaseSocialProvider = 'google' | 'apple';

@Injectable({ providedIn: 'root' })
export class GoogleFirebaseAuthService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private persistenceReady: Promise<void> | null = null;
  private readonly authResolver = browserPopupRedirectResolver;

  isConfigured(): boolean {
    return !!environment.firebase?.apiKey && !!environment.firebase?.projectId;
  }

  private ensureAuth(): Auth {
    if (!this.isConfigured()) {
      throw new Error('Firebase is not configured for Google email OTP.');
    }

    const sharedApp = getSharedFirebaseApp();
    const sharedAuth = getSharedFirebaseAuth();
    if (sharedApp && sharedAuth) {
      this.app = sharedApp;
      this.auth = sharedAuth;
      return sharedAuth;
    }

    if (!this.app) {
      const firebaseOptions = getFirebaseInitOptions();
      if (!firebaseOptions) {
        throw new Error('Firebase is not configured for Google email OTP.');
      }
      this.app = getApps().length
        ? getApp()
        : initializeApp(firebaseOptions);
      this.auth = getAuth(this.app);
      this.persistenceReady = setPersistence(this.auth, browserLocalPersistence);
    }
    return this.auth!;
  }

  hasPendingRedirectParams(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const { href, search, hash } = window.location;
    return [href, search, hash].some(
      (part) =>
        part.includes('apiKey=') ||
        part.includes('authType=') ||
        part.includes('authUser=') ||
        /[#?&]state=/.test(part)
    );
  }

  async getCurrentUser(): Promise<User | null> {
    if (!this.isConfigured()) {
      return null;
    }
    const auth = this.ensureAuth();
    await awaitFirebasePersistenceReady();
    if (this.persistenceReady) {
      await this.persistenceReady;
    }
    return auth.currentUser;
  }

  async signInWithCustomToken(customToken: string): Promise<UserCredential> {
    const auth = this.ensureAuth();
    await awaitFirebasePersistenceReady();
    if (this.persistenceReady) {
      await this.persistenceReady;
    }
    return signInWithCustomToken(auth, customToken);
  }

  async resolveSessionTokens(
    user: User,
    forceRefresh = false
  ): Promise<{ idToken: string; refreshToken: string; email: string }> {
    const idToken = await this.getIdToken(user, forceRefresh);
    return {
      idToken,
      refreshToken: user.refreshToken?.trim() || '',
      email: user.email?.trim().toLowerCase() || '',
    };
  }

  async signOut(): Promise<void> {
    if (!this.isConfigured()) {
      return;
    }
    const auth = this.ensureAuth();
    await awaitFirebasePersistenceReady();
    if (this.persistenceReady) {
      await this.persistenceReady;
    }
    await signOut(auth);
  }

  async signInWithSocialRedirect(provider: FirebaseSocialProvider): Promise<void> {
    const auth = this.ensureAuth();
    await awaitFirebasePersistenceReady();
    if (this.persistenceReady) {
      await this.persistenceReady;
    }
    const firebaseProvider =
      provider === 'google'
        ? this.buildGoogleProvider()
        : this.buildAppleProvider();
    await signInWithRedirect(auth, firebaseProvider, this.authResolver);
  }

  async completeRedirectSignIn(): Promise<{
    user: User | null;
    error: Error | null;
  }> {
    if (!this.isConfigured()) {
      return { user: null, error: null };
    }

    const boot = getFirebaseRedirectBootstrapState();
    if (boot.completed) {
      if (boot.user) {
        return { user: boot.user, error: boot.error };
      }
      if (boot.error) {
        return { user: null, error: boot.error };
      }
      const persisted = await this.getCurrentUser();
      return { user: persisted, error: null };
    }

    try {
      const auth = this.ensureAuth();
      await awaitFirebasePersistenceReady();
      if (this.persistenceReady) {
        await this.persistenceReady;
      }
      const result: UserCredential | null = await getRedirectResult(
        auth,
        this.authResolver
      );
      if (result?.user) {
        return { user: result.user, error: null };
      }
      const persisted = await this.getCurrentUser();
      return { user: persisted, error: null };
    } catch (error) {
      return { user: null, error: error as Error };
    }
  }

  async getIdToken(user: User, forceRefresh = false): Promise<string> {
    const token = await user.getIdToken(forceRefresh);
    if (!token?.trim()) {
      throw new Error('Firebase did not return an ID token.');
    }
    return token;
  }

  private buildGoogleProvider(): GoogleAuthProvider {
    const provider = new GoogleAuthProvider();
    // Always show Google's account chooser, even when only one account is signed in.
    provider.setCustomParameters({ prompt: 'select_account' });
    return provider;
  }

  private buildAppleProvider(): OAuthProvider {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return provider;
  }

  private emailLinkStorageKey = 'he_emailForSignIn';

  private buildEmailLinkActionCodeSettings(): ActionCodeSettings {
    const url =
      typeof window !== 'undefined'
        ? `${window.location.origin}/login`
        : 'https://dev-auth.healthexam.ai/login';
    return {
      url,
      handleCodeInApp: true,
    };
  }

  /** Firebase passwordless email — send sign-in link (Firebase delivers the email). */
  async sendPasswordlessEmailLink(email: string): Promise<void> {
    const auth = this.ensureAuth();
    await awaitFirebasePersistenceReady();
    if (this.persistenceReady) {
      await this.persistenceReady;
    }
    const normalized = email.trim().toLowerCase();
    try {
      await sendSignInLinkToEmail(
        auth,
        normalized,
        this.buildEmailLinkActionCodeSettings()
      );
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || '';
      if (
        code === 'auth/operation-not-allowed' ||
        /OPERATION_NOT_ALLOWED/i.test(String((err as Error)?.message || ''))
      ) {
        throw new Error(
          'Passwordless email sign-in is disabled in Firebase. In Firebase Console → Authentication → Sign-in method → Email/Password, enable both "Email/Password" and "Email link (passwordless sign-in)", then Save. Project: health-exam-ai-apps.'
        );
      }
      throw err;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(this.emailLinkStorageKey, normalized);
    }
  }

  isEmailLinkSignInReturn(): boolean {
    if (typeof window === 'undefined' || !this.isConfigured()) {
      return false;
    }
    const auth = this.ensureAuth();
    return isSignInWithEmailLink(auth, window.location.href);
  }

  readEmailFromSignInLinkUrl(): string {
    return this.readPasswordlessLinkParam('heEmail').toLowerCase();
  }

  readFlowTokenFromSignInLinkUrl(): string {
    return this.readPasswordlessLinkParam('heFlowToken');
  }

  readFlowKindFromSignInLinkUrl(): 'login' | 'register' {
    return this.readPasswordlessLinkParam('heFlowKind').toLowerCase() ===
      'register'
      ? 'register'
      : 'login';
  }

  /** Read heEmail / heFlowToken from /login query or nested continueUrl. */
  private readPasswordlessLinkParam(name: string): string {
    if (typeof window === 'undefined') {
      return '';
    }
    const params = new URLSearchParams(window.location.search);
    const direct = params.get(name)?.trim();
    if (direct) {
      return direct;
    }

    const continueUrl = params.get('continueUrl')?.trim();
    if (!continueUrl) {
      return '';
    }

    try {
      return new URL(continueUrl).searchParams.get(name)?.trim() || '';
    } catch {
      return '';
    }
  }

  /** Email verification / other Firebase email action links (mode=verifyEmail, etc.). */
  readFirebaseEmailActionFromUrl(): { mode: string; oobCode: string } | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode')?.trim() || '';
    const oobCode = params.get('oobCode')?.trim() || '';
    if (!mode || !oobCode) {
      return null;
    }
    return { mode, oobCode };
  }

  async applyEmailVerificationCode(oobCode: string): Promise<void> {
    const auth = this.ensureAuth();
    await awaitFirebasePersistenceReady();
    if (this.persistenceReady) {
      await this.persistenceReady;
    }
    await applyActionCode(auth, oobCode);
  }

  clearFirebaseEmailActionQueryParams(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    [
      'mode',
      'oobCode',
      'apiKey',
      'continueUrl',
      'lang',
      'tenantId',
      'heEmail',
      'heFlowToken',
      'heFlowKind',
    ].forEach((key) => url.searchParams.delete(key));
    const next = url.pathname + url.search + url.hash;
    window.history.replaceState({}, '', next || '/login');
  }

  readStoredEmailForSignInLink(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.localStorage.getItem(this.emailLinkStorageKey)?.trim() || '';
  }

  storeEmailForSignInLink(email: string): void {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        this.emailLinkStorageKey,
        email.trim().toLowerCase()
      );
    }
  }

  clearStoredEmailForSignInLink(): void {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(this.emailLinkStorageKey);
    }
  }

  /** Complete passwordless email sign-in after the user opens the link. */
  async completeEmailLinkSignIn(
    email?: string
  ): Promise<UserCredential | null> {
    if (!this.isEmailLinkSignInReturn()) {
      return null;
    }
    const auth = this.ensureAuth();
    await awaitFirebasePersistenceReady();
    if (this.persistenceReady) {
      await this.persistenceReady;
    }

    const resolvedEmail =
      email?.trim().toLowerCase() ||
      this.readEmailFromSignInLinkUrl() ||
      this.readStoredEmailForSignInLink() ||
      '';
    if (!resolvedEmail) {
      throw new Error(
        'Enter the same email address where the sign-in link was sent.'
      );
    }

    const cred = await signInWithEmailLink(
      auth,
      resolvedEmail,
      window.location.href
    );
    this.clearStoredEmailForSignInLink();
    return cred;
  }
}
